import React, { ReactElement, useContext, useState } from 'react'
import { MIN_NUMBER_OF_GUESTS, useWizardSelector } from '@/components/ui/wizard/WizardState'

import { useDispatch } from 'react-redux'
import Modal from '@/components/common/Modal'
import Price from '@/components/ui/wizard/filter/Price'
import Guests from '@/components/ui/wizard/filter/Guests'
import Rating, { RatingValues } from '@/components/ui/wizard/filter/Rating'
import SelectOptions from '@/components/ui/wizard/filter/SelectOptions'
import FilterControlButtons from '@/components/ui/wizard/filter/FilterControlButtons'
import { ConfigContext } from '@/components/common/ConfigContext'
import { Currency } from '@/types/Page'
import DropDown from '@/components/ui/wizard/filter/DropDown'
import { FilterModalType } from '@/lib/sharedConstants'
import {
  setLocationFilter,
  setMaximumPrice,
  setNumberOfGuests,
  setRating,
  setStars,
} from '@/components/ui/wizard/WizardActions'
import { LocationFilterElement } from '../../../../domain-config/domain-config'
import Stars, { StarsValues } from '@/components/ui/wizard/filter/Stars'
import { useTranslations } from 'next-intl'

const LABELS = {
  [FilterModalType.ALL]: 'buttonFilters',
  [FilterModalType.PRICE]: 'buttonPrice',
  [FilterModalType.GUESTS]: 'buttonGuestPlural',
  [FilterModalType.RATING]: 'buttonRating',
  [FilterModalType.STARS]: 'buttonStars',
  [FilterModalType.LOCATION]: 'buttonLocation',
  [FilterModalType.ORDER]: 'buttonAllSorting',
  [FilterModalType.ORDER_MOBILE]: 'buttonAllSorting',
}

function Filter(props: FilterProps): ReactElement {
  const config = useContext(ConfigContext)!

  const selectOptions = {
    landscape: config.searchFilters.location,
    ...config.searchFilters.additionalFilters,
  }

  if (!props.currency) {
    return <></>
  }

  return (
    <div className="relative mb-10 px-10 py-8 lg:mb-0 lg:px-0 lg:py-0">
      <Price
        maximumPrice={props.maximumPrice}
        setMaximumPriceLocalState={props.setMaximumPriceLocalState}
        currency={props.currency}
      />
      <Guests
        numberOfGuests={props.numberOfGuests}
        setNumberOfGuests={props.setNumberOfGuestsLocalState}
      />
      <Rating rating={props.rating} setRatingLocalState={props.setRatingLocalState} />
      <Stars stars={props.stars} setStarsLocalState={props.setStarsLocalState} />
      <SelectOptions
        selectedLocationFilter={props.locationFilter}
        setLocationFilterLocalState={props.setLocationFilterLocalState}
        options={selectOptions}
      />
    </div>
  )
}

interface FilterModalProps {
  currency: Currency
  type: FilterModalType | undefined
  close: () => void
}

interface FilterProps {
  currency: Currency | undefined
  setLocationFilterLocalState: (r: LocationFilterElement[]) => void
  locationFilter: LocationFilterElement[]
  rating: RatingValues | undefined
  stars: StarsValues | undefined
  setRatingLocalState: (r: RatingValues) => void
  setStarsLocalState: (r: StarsValues) => void
  numberOfGuests: number
  setNumberOfGuestsLocalState: (n: number) => void
  setMaximumPriceLocalState: (p: string) => void
  maximumPrice: string | undefined
}

export default function FilterModal(props: FilterModalProps): ReactElement {
  const dispatch = useDispatch()
  const numberOfGuestInStore = useWizardSelector((state) => state.numberOfGuests)
  const [numberOfGuests, setNumberOfGuestsLocalState] = useState(numberOfGuestInStore)
  const ratingInStore = useWizardSelector((state) => state.rating)
  const starsInStore = useWizardSelector((state) => state.stars)
  const [rating, setRatingLocalState] = useState(ratingInStore)
  const [stars, setStarsLocalState] = useState(starsInStore)

  const locationFilterInStore = useWizardSelector((state) => state.locationFilter)
  const [locationFilter, setLocationFilterLocalState] = useState(locationFilterInStore)
  const maximumPriceInStore = useWizardSelector((state) => state.maximumPrice)
  const [maximumPrice, setMaximumPriceLocalState] = useState(maximumPriceInStore)
  const t = useTranslations('SearchPage')

  function apply(): void {
    props.close()
    dispatch(setNumberOfGuests(numberOfGuests))
    dispatch(setRating(rating))
    dispatch(setStars(stars))
    dispatch(setLocationFilter(locationFilter))
    dispatch(setMaximumPrice(maximumPrice))
  }

  function reset(): void {
    setNumberOfGuestsLocalState(MIN_NUMBER_OF_GUESTS)
    setRatingLocalState(undefined)
    setStarsLocalState(undefined)
    setLocationFilterLocalState([])
    setMaximumPriceLocalState(undefined)
  }

  return (
    <Modal
      visible={props.type === FilterModalType.ALL}
      close={apply}
      heading={props.type ? t(LABELS[props.type]) : ''}
      classNameAdditionalBody="h-[100%] rounded-none sm:max-w-[100%] w-[100%] lg:w-[680px] lg:max-h-[890px]"
      additionalClassName={'h-full z-[101]'}
      mobileFullscreen={true}
    >
      <Filter
        {...props}
        locationFilter={locationFilter}
        setLocationFilterLocalState={setLocationFilterLocalState}
        setRatingLocalState={setRatingLocalState}
        setStarsLocalState={setStarsLocalState}
        rating={rating}
        stars={stars}
        numberOfGuests={numberOfGuests}
        setNumberOfGuestsLocalState={setNumberOfGuestsLocalState}
        maximumPrice={maximumPrice}
        setMaximumPriceLocalState={setMaximumPriceLocalState}
      />
      <FilterControlButtons reset={reset} apply={apply} resetLabel={t('filterOptionReset')} />
    </Modal>
  )
}

interface FilterDropDownProps {
  currency: Currency | undefined
}

export function FilterDropdown(props: FilterDropDownProps): ReactElement {
  const dispatch = useDispatch()
  const numberOfGuestInStore = useWizardSelector((state) => state.numberOfGuests)
  const [numberOfGuests, setNumberOfGuestsLocalState] = useState(numberOfGuestInStore)
  const ratingInStore = useWizardSelector((state) => state.rating)
  const starsInStore = useWizardSelector((state) => state.stars)
  const [rating, setRatingLocalState] = useState(ratingInStore)
  const [stars, setStarsLocalState] = useState(starsInStore)
  const locationFilterInStore = useWizardSelector((state) => state.locationFilter)
  const [locationFilter, setLocationFilterLocalState] = useState(locationFilterInStore)
  const maximumPriceInStore = useWizardSelector((state) => state.maximumPrice)
  const [maximumPrice, setMaximumPriceLocalState] = useState(maximumPriceInStore)
  const t = useTranslations('SearchPage')

  function apply(): void {
    dispatch(setNumberOfGuests(numberOfGuests))
    dispatch(setRating(rating))
    dispatch(setStars(stars))
    dispatch(setLocationFilter(locationFilter))
    dispatch(setMaximumPrice(maximumPrice))
  }

  function reset(): void {
    setNumberOfGuestsLocalState(MIN_NUMBER_OF_GUESTS)
    setRatingLocalState(undefined)
    setStarsLocalState(undefined)
    setLocationFilterLocalState([])
    setMaximumPriceLocalState(undefined)
  }

  return (
    <DropDown
      reset={reset}
      apply={apply}
      resetLabel={t('filterOptionReset')}
      rightAligned={true}
      id="order"
      additionalClassName="translate-x-[-44px] xxl:translate-x-[0px] xl:translate-x-[-50px]"
    >
      <div className="hidden max-h-[calc(100vh-200px)] overflow-x-hidden lg:block">
        <Filter
          {...props}
          locationFilter={locationFilter}
          setLocationFilterLocalState={setLocationFilterLocalState}
          setRatingLocalState={setRatingLocalState}
          setStarsLocalState={setStarsLocalState}
          rating={rating}
          stars={stars}
          numberOfGuests={numberOfGuests}
          setNumberOfGuestsLocalState={setNumberOfGuestsLocalState}
          maximumPrice={maximumPrice}
          setMaximumPriceLocalState={setMaximumPriceLocalState}
        />
      </div>
    </DropDown>
  )
}
